import React, { useState } from "react";
import {
  Pressable,
  View,
  StyleSheet,
  Image,
  Modal,
  Linking,
} from "react-native";
import { Button, HelperText, Icon, IconButton, Text } from "react-native-paper";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { useTranslation } from "react-i18next";

import { FieldType } from "../../config/companies/config";

interface FileFieldProps {
  field: {
    name: string;
    label: string;
    type?: FieldType;
  };
  value: any;
  onChange: (fieldName: string, newValue: any) => void;
  errors?: { [key: string]: string };
}

const FileField = ({ field, value, onChange, errors }: FileFieldProps) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fieldType = field.type || "file"; // Default to "file" for backward compatibility

  const appendFiles = (
    files: { uri: string; name: string; mimeType: string | null }[],
  ) => {
    const currentValue = value ? (Array.isArray(value) ? value : [value]) : [];
    onChange(field.name, [...currentValue, ...files]);
  };

  const pickMedia = async (allowVideos = true) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: allowVideos ? ["images", "videos"] : ["images"],
      allowsMultipleSelection: true,
      quality: 1,
    });

    if (result.assets) {
      appendFiles(
        result.assets.map((asset) => ({
          uri: asset.uri,
          name: asset.fileName || `file_${Date.now()}`,
          mimeType: asset.type ? `${asset.type}/*` : null,
        })),
      );
    }
  };

  const pickAnyDocument = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: "*/*", // Allow all file types
      multiple: true,
      copyToCacheDirectory: true,
    });

    if (!result.canceled && result.assets) {
      appendFiles(
        result.assets.map((asset) => ({
          uri: asset.uri,
          name: asset.name || `file_${Date.now()}`,
          mimeType: asset.mimeType || null,
        })),
      );
    }
  };

  const pickDocument = async () => {
    try {
      // Use document picker for "document" type (allows any file type)
      // Use image picker for "file" type (images and videos only)
      if (fieldType === "document") {
        await pickAnyDocument();
      } else {
        // Use image picker for "file" type (backward compatibility)
        await pickMedia();
      }
    } catch (err) {
      console.error("Error picking file:", err);
      alert(t("Failed to pick file"));
    }
  };

  const pickFileAndDocumentPhoto = async () => {
    try {
      await pickMedia(false);
    } catch (err) {
      console.error("Error picking media:", err);
      alert(t("Failed to pick file"));
    }
  };

  const pickFileAndDocumentFile = async () => {
    try {
      await pickAnyDocument();
    } catch (err) {
      console.error("Error picking document:", err);
      alert(t("Failed to pick file"));
    }
  };

  const removeFile = (file: { uri: string } | string) => {
    const currentValue = Array.isArray(value) ? value : [value];

    if (typeof file === "string") {
      onChange(
        field.name,
        currentValue.filter((f) => f !== file),
      );
    } else {
      onChange(
        field.name,
        currentValue.filter((f) => f.uri !== file.uri),
      );
    }
  };

  const handleFilePress = async (
    file:
      | {
          uri: string;
          mimeType: string | null;
        }
      | string,
  ) => {
    try {
      if (typeof file === "string") {
        await Linking.openURL(file);
      } else {
        const isImage = file.mimeType?.startsWith("image/");

        if (isImage) {
          setSelectedImage(file.uri);
        } else {
          const supported = await Linking.canOpenURL(file.uri);
          if (supported) {
            await Linking.openURL(file.uri);
          } else {
            alert(t("This file type is not supported on your device"));
          }
        }
      }
    } catch (err) {
      console.error("Error opening file:", err);
      alert(t("Failed to open file"));
    }
  };

  // Safely render files - ensuring null/undefined values don't cause issues
  const renderFiles = () => {
    if (!value) return null;

    const files = Array.isArray(value) ? value : [value];
    if (!files.length) return null;

    return (
      <View style={styles.previewContainer}>
        {files.map((file, index) => (
          <View key={index} style={styles.fileWrapper}>
            <Pressable
              style={styles.fileInfoButton}
              onPress={() => handleFilePress(file)}
            >
              <Icon size={36} source="file-document-multiple-outline" />
              <Text numberOfLines={1} style={styles.fileName}>
                {typeof file === "string"
                  ? `file ${index + 1}`
                  : file && file.name
                    ? file.name
                    : `file ${index + 1}`}
              </Text>
            </Pressable>
            <IconButton
              icon="delete-outline"
              mode="contained-tonal"
              containerColor="#FEE2E2"
              iconColor="#B91C1C"
              onPress={() => removeFile(file)}
              accessibilityLabel={t("Delete")}
              style={styles.removeButton}
            />
          </View>
        ))}
      </View>
    );
  };

  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
          justifyContent: "space-between",
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
          <View style={{ transform: [{ rotate: "135deg" }] }}>
            <Icon size={24} source="attachment" />
          </View>
          <Text variant="titleMedium">{t("Attachments")}</Text>
        </View>
        {fieldType === "fileAndDocument" ? (
          <View style={styles.actionsRow}>
            <Button
              onPress={pickFileAndDocumentPhoto}
              style={{ borderRadius: 4 }}
              contentStyle={{ marginHorizontal: -10, marginVertical: -3 }}
              mode="contained-tonal"
              icon="image-plus"
            >
              <Text>{t("Photo")}</Text>
            </Button>
            <Button
              onPress={pickFileAndDocumentFile}
              style={{ borderRadius: 4 }}
              contentStyle={{ marginHorizontal: -10, marginVertical: -3 }}
              mode="contained-tonal"
              icon="file-plus"
            >
              <Text>{t("File")}</Text>
            </Button>
          </View>
        ) : (
          <Button
            onPress={pickDocument}
            style={{ borderRadius: 4 }}
            contentStyle={{ marginHorizontal: -10, marginVertical: -3 }}
            mode="contained-tonal"
          >
            <Text>{t("Add")}</Text>
          </Button>
        )}
      </View>

      {renderFiles()}

      {errors?.[field.name] && (
        <HelperText type="error">{t(errors[field.name])}</HelperText>
      )}

      <Modal
        visible={!!selectedImage}
        transparent={true}
        onRequestClose={() => setSelectedImage(null)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Image
              source={{ uri: selectedImage || "" }}
              style={styles.modalImage}
              resizeMode="contain"
            />
            <Button
              mode="contained"
              onPress={() => setSelectedImage(null)}
              style={styles.closeButton}
            >
              Закрыть
            </Button>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  fileButton: {
    marginVertical: 8,
  },
  actionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  previewContainer: {
    marginTop: 8,
    gap: 8,
  },
  fileWrapper: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 10,
    paddingRight: 4,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  fileInfoButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
    gap: 10,
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    marginRight: 8,
  },
  removeButton: {
    marginLeft: 6,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
    width: "90%",
    maxHeight: "90%",
  },
  modalImage: {
    width: "100%",
    height: 400,
    marginBottom: 10,
  },
  closeButton: {
    marginTop: 10,
  },
});

export default FileField;
